/**
 * Staggered cantilever generator (plan §4.1, §4.7; taxonomy §1.4): design → BuildModel.
 * Pure, synchronous; never throws for a design-shaped input (bad values become errors).
 *
 * Layout (ticks, origin floor / left / back): horizontals k = 0…tiers (bottom, shelves, top)
 * at full depth from xStart·W to xEnd·W; tier i sits between horizontals i and i + 1. One
 * continuous divider runs from the top face of the bottom to the underside of the top and is
 * housed through every shelf it crosses. End panels (full depth, at x = 0 or W − t) stand
 * between the horizontals of their tier; **merge rule**: panels on the same side of adjacent
 * tiers become one board when the horizontal between them does not reach that end. Posts
 * stand at the front, `postInset·W` in from the end. Back blocks sit at the back between the
 * horizontals, beside the divider.
 */
import type { BuildModel, Design, Issue } from '../../domain/schemas';
import type { ConstructionProfile } from '../../domain/profiles';
import { fmtMm } from '../../domain/units';
import { assembleBuild, evenSplit, familyJoints, familyStock, fracTicks, layable, minHeightFix, sizeErrors, splitByWeights, THIN_STOCK_ID, type FamilyBoard, type FamilyJoint, type FamilyOpening } from '../build-common';
import { parseConstructionProfile } from '../../domain/profiles';
import { resolveStock, STOCKS, type AnyStock } from '../stock';
import jointsJson from './joints.json' with { type: 'json' };
import { paramsOf, STAGGERED_ID, STAGGERED_LABEL, STAGGERED_STOCK, STAGGERED_VERSION, type StaggeredParams, type TierEnds } from './design';
import type { Hardware, IssueFix } from '../assembly';
import { baseErrors, baseOf, feetHardware, footKindFor, liftOf } from '../base';

const parsed = parseConstructionProfile(jointsJson);
if (!parsed.ok) throw new Error(`staggered-cantilever joints.json is invalid: ${parsed.errors.join('; ')}`);
export const STAGGERED_JOINTS: ConstructionProfile = Object.freeze(parsed.value) as ConstructionProfile;

interface Seg { id: string; x0: number; x1: number; fix?: IssueFix }

/** Supports of a horizontal: the verticals touching it (from below or above), as x-intervals. */
export interface Measures {
  t: number;
  levels: number[];
  clears: number[];
  xs: { x0: number; x1: number }[];
  divider: { x0: number; x1: number } | null;
}

const ov = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);

/** Parameter-block errors before anything is laid out. */
export function paramErrors(p: StaggeredParams): Issue[] {
  const e: Issue[] = [];
  if (!Number.isInteger(p.tiers) || p.tiers < 2 || p.tiers > 8) e.push({ field: 'tiers', message: 'Tiers must be between 2 and 8.' });
  const n = Math.max(0, p.tiers | 0);
  if (!Array.isArray(p.horizontals) || p.horizontals.length !== n + 1) e.push({ field: 'horizontals', message: `${n + 1} horizontals are needed for ${n} tiers; ${p.horizontals?.length ?? 0} are set.` });
  if (!Array.isArray(p.tierEnds) || p.tierEnds.length !== n) e.push({ field: 'tierEnds', message: `${n} tier ends are needed; ${p.tierEnds?.length ?? 0} are set.` });
  if (p.tierWeights && (p.tierWeights.length !== n || p.tierWeights.some((w) => !(w > 0)))) e.push({ field: 'tierWeights', message: `Tier heights need ${n} positive values.` });
  (p.horizontals ?? []).forEach((h, k) => {
    if (!(h.xStart >= 0 && h.xEnd <= 1 && h.xEnd - h.xStart > 0.05)) e.push({ field: `horizontal-${k}`, message: `Horizontal ${k + 1} must start before it ends, inside the overall width.` });
  });
  if (p.dividerX !== null && !(p.dividerX > 0.05 && p.dividerX < 0.95)) e.push({ field: 'dividerX', message: 'The divider must sit inside the width.' });
  if (!(p.blockWidth > 0 && p.blockWidth < 0.5)) e.push({ field: 'blockWidth', message: 'Back blocks must be narrower than half the width.' });
  if (p.tierDividers && (!Array.isArray(p.tierDividers) || p.tierDividers.length > n)) e.push({ field: 'tierDividers', message: `Per-tier dividers are listed for ${p.tierDividers.length} tiers; there are ${n}.` });
  (p.tierDividers ?? []).forEach((xs, i) => {
    if (!Array.isArray(xs) || xs.some((x) => !(x > 0.02 && x < 0.98))) e.push({ field: `tier-${i}`, message: `Tier ${i + 1}'s dividers must stand inside the width.`, fix: { label: `Clear tier ${i + 1}'s dividers`, patch: { tierDividers: (p.tierDividers ?? []).map((row, k) => (k === i ? [] : row.slice())) } } });
  });
  return e;
}

/** The tier ends with one end panel or post switched off (the fix for one that meets nothing). */
const without = (p: StaggeredParams, tier: number, key: keyof Omit<TierEnds, 'backBlock'>): IssueFix => ({
  label: `Leave out the ${key.startsWith('left') ? 'left' : 'right'} ${key.endsWith('Panel') ? 'end panel' : 'post'} of tier ${tier + 1}`,
  patch: { tierEnds: p.tierEnds.map((e, i) => (i === tier ? { ...e, [key]: false } : { ...e })) },
});

const tierLabel = (a: number, b: number) => (a === b ? `tier ${a + 1}` : `tiers ${a + 1}–${b + 1}`);

export function generateStaggered(design: Design, stock?: AnyStock | null, construction?: ConstructionProfile): BuildModel {
  const c = familyJoints(STAGGERED_ID, construction, STAGGERED_JOINTS);
  const S = familyStock(design, stock, STOCKS, STAGGERED_STOCK);
  const R = resolveStock(S);
  const t = R.thicknessTicks;
  const p = paramsOf(design);
  const { W, H, D } = design;
  const shapeErrors = paramErrors(p);
  const errors: Issue[] = [...sizeErrors(design, c), ...shapeErrors];
  const warnings: Issue[] = [];
  const boards: FamilyBoard[] = [];
  const joints: FamilyJoint[] = [];
  const openings: FamilyOpening[] = [];
  const datums: { partId: string; label: string; ticks: number }[] = [];
  const minOpening = c.envelope.minOpeningTicks;
  const maxRatio = c.limits?.maxCantileverRatio ?? 0.35;
  const maxSpan = Math.round(c.warnings.maxShelfSpanMm * 10);

  // what the stack stands on (FND-08): plinth rails under the bottom, or levelling feet
  const foot = footKindFor(R.kind);
  const base = baseOf(p, foot);
  const lift = liftOf(base);
  if (p.base !== undefined) errors.push(...baseErrors(base, D, foot));
  // a staggered stack has no full-height sides to run down to the floor
  if (base.kind === 'sides') errors.push({ field: 'base', message: 'A staggered shelf has no full-height sides to stand on; choose the floor, a plinth or feet.', fix: { label: 'Stand it on the floor', patch: { base: 'none' } } });
  const hardware: Hardware[] = [];

  // a size out of range or a failed base rule never empties the model: lay out whenever the block is
  // well formed and the sizes are positive (pilot-v1-r2 p11)
  const structural = shapeErrors.length === 0 && layable(design);
  const n = structural ? p.tiers : 0;
  if (structural) {
    // --- levels -------------------------------------------------------------------------------
    const A = H - lift - (n + 1) * t;
    const clears = p.tierWeights ? splitByWeights(A, p.tierWeights) : evenSplit(A, n);
    const thinT = resolveStock(STOCKS[THIN_STOCK_ID]!).thicknessTicks;
    clears.forEach((g, i) => {
      if (g >= minOpening) return;
      // fix: one tier fewer, else thinner stock, else the height that gives every tier the minimum
      const share = p.tierWeights ? p.tierWeights[i]! / p.tierWeights.reduce((a, b) => a + b, 0) : 1 / n;
      const need = Math.ceil((minOpening / share + lift + (n + 1) * t) / 10) * 10;
      const fix = minHeightFix({
        count: n, minCount: 2, key: 'tiers', noun: 'tier', t, thinT, min: minOpening, currentStock: S.id,
        clearWith: (k, tt) => (k === n && p.tierWeights ? Math.min(...splitByWeights(H - lift - (k + 1) * tt, p.tierWeights)) : Math.floor((H - lift - (k + 1) * tt) / k)),
      }) ?? { label: `Set height to ${fmtMm(need)} mm`, patch: { H: need } };
      errors.push({ field: `tier-${i}`, message: `Tier ${i + 1} would be ${fmtMm(g)} mm high; the minimum clear height is ${fmtMm(minOpening)} mm.`, fix });
    });
    // tiers that leave no room still lay out at 1 mm so the parts show beside the error
    clears.forEach((g, i) => { clears[i] = Math.max(g, 10); });
    const levels: number[] = [];
    let y = lift;
    for (let k = 0; k <= n; k++) { levels.push(y); y += t + (clears[k] ?? 0); }

    // --- horizontals --------------------------------------------------------------------------
    const hId = (k: number) => (k === 0 ? 'bottom' : k === n ? 'top' : `shelf-${k}`);
    const hLabel = (k: number) => (k === 0 ? 'Bottom' : k === n ? 'Top' : `Shelf ${k}`);
    const xs = p.horizontals.map((h) => ({ x0: fracTicks(W, h.xStart), x1: fracTicks(W, h.xEnd) }));
    for (let k = 0; k <= n; k++) {
      const { x0, x1 } = xs[k]!;
      const free = [x0 > 0 ? 'left' : '', x1 < W ? 'right' : ''].filter(Boolean);
      boards.push({
        id: hId(k), role: k === 0 ? 'bottom' : k === n ? 'top' : 'shelf', label: hLabel(k), l: x1 - x0, w: D, t, grainWorld: 'x',
        box: { x: x0, y: levels[k]!, z: 0, sx: x1 - x0, sy: t, sz: D },
        stock: 'carcass', faceSide: 'up',
        ...(k > 0 && k < n ? { datum: levels[k]!, shelfNo: k } : {}),
        ...(free.length ? { machining: `free ${free.join(' and ')} end${free.length > 1 ? 's' : ''}: end grain sealed, 1 mm arris` } : {}),
      });
      if (k > 0 && k < n) datums.push({ partId: hId(k), label: hLabel(k), ticks: levels[k]! });
    }
    const yTop = (k: number) => levels[k]! + t; // top face of horizontal k
    /** Verticals per horizontal: what stands on it (above) and under it (below). */
    const below: Seg[][] = Array.from({ length: n + 1 }, () => []);
    const above: Seg[][] = Array.from({ length: n + 1 }, () => []);
    const standOn = (seg: Seg, kLow: number, kHigh: number, kind: FamilyJoint['kind'], label: string) => {
      for (const [k, face, list] of [[kLow, 'top', above], [kHigh, 'underside', below]] as const) {
        const h = xs[k]!;
        if (ov(seg.x0, seg.x1, h.x0, h.x1) < (seg.x1 - seg.x0) - 0.5) {
          errors.push({ field: seg.id, message: `${label} ${face === 'top' ? 'stands on' : 'meets'} nothing: ${hLabel(k).toLowerCase()} does not reach it.`, ...(seg.fix ? { fix: seg.fix } : {}) });
          continue;
        }
        list[k]!.push(seg);
        joints.push({ a: seg.id, b: hId(k), kind, face, datum: face === 'top' ? yTop(k) : levels[k]! });
      }
    };

    // --- divider ------------------------------------------------------------------------------
    let divider: { x0: number; x1: number } | null = null;
    if (p.dividerX !== null) {
      const cx = fracTicks(W, p.dividerX);
      divider = { x0: cx - Math.round(t / 2), x1: cx - Math.round(t / 2) + t };
      const len = levels[n]! - lift - t;
      boards.push({ id: 'divider', role: 'divider', label: 'Divider', l: len, w: D, t, grainWorld: 'y', box: { x: divider.x0, y: lift + t, z: 0, sx: t, sy: len, sz: D }, stock: 'carcass', faceSide: 'out', machining: n > 1 ? `housed through ${n - 1} shel${n - 1 === 1 ? 'f' : 'ves'}, ${fmtMm(Math.round(t / 3))} mm each side` : undefined });
      const seg = { id: 'divider', ...divider };
      for (const k of [0, n]) {
        const h = xs[k]!;
        if (ov(divider.x0, divider.x1, h.x0, h.x1) < t - 0.5) errors.push({ field: 'dividerX', message: `The divider ${k === 0 ? 'stands on' : 'meets'} nothing: the ${k === 0 ? 'bottom' : 'top'} does not reach it.` });
        else {
          (k === 0 ? above : below)[k]!.push(seg);
          joints.push({ a: 'divider', b: hId(k), kind: 'dowel-butt', face: k === 0 ? 'top' : 'underside', datum: k === 0 ? lift + t : levels[n]! });
        }
      }
      for (let k = 1; k < n; k++) {
        const h = xs[k]!;
        const o = ov(divider.x0, divider.x1, h.x0, h.x1);
        if (o >= t - 0.5) {
          below[k]!.push(seg); above[k]!.push(seg);
          joints.push({ a: hId(k), b: 'divider', kind: 'housed-dado', face: 'crossing', datum: levels[k]! });
        } else if (o > -0.5) {
          errors.push({ field: `horizontal-${k}`, message: `${hLabel(k)} ends at the divider; run it through the divider or stop it clear of it.` });
        }
      }
    }

    // --- end panels (merge rule) ----------------------------------------------------------------
    // A panel closes its tier's end: it stands at the end of the shorter of the two horizontals
    // it joins (the outer edge when both reach it). Panels on the same side of adjacent tiers
    // become one board when the horizontal between them stops clear of the panel.
    for (const side of ['left', 'right'] as const) {
      const key = side === 'left' ? 'leftPanel' : 'rightPanel';
      const at = (lo: number, hi: number) => (side === 'left' ? Math.max(xs[lo]!.x0, xs[hi]!.x0) : Math.min(xs[lo]!.x1, xs[hi]!.x1) - t);
      let i = 0;
      while (i < n) {
        if (!p.tierEnds[i]![key]) { i++; continue; }
        let j = i;
        while (j + 1 < n && p.tierEnds[j + 1]![key]) {
          const px = at(i, j + 2);
          let clear = true;
          for (let k = i + 1; k <= j + 1; k++) if (ov(px, px + t, xs[k]!.x0, xs[k]!.x1) > 0) clear = false;
          if (!clear) break;
          j++;
        }
        const px0 = at(i, j + 1);
        const id = `end-${side[0]}-${i + 1}`;
        const y0 = yTop(i), y1 = levels[j + 1]!;
        const label = `${side === 'left' ? 'Left' : 'Right'} end, ${tierLabel(i, j)}`;
        boards.push({ id, role: 'end', label, l: y1 - y0, w: D, t, grainWorld: 'y', box: { x: px0, y: y0, z: 0, sx: t, sy: y1 - y0, sz: D }, stock: 'carcass', faceSide: 'out' });
        standOn({ id, x0: px0, x1: px0 + t, fix: without(p, i, key) }, i, j + 1, 'dowel-butt', label);
        for (let k = i + 1; k <= j; k++) if (ov(px0, px0 + t, xs[k]!.x0, xs[k]!.x1) > 0) errors.push({ field: id, message: `${label} runs into ${hLabel(k).toLowerCase()}.`, fix: without(p, i, key) });
        i = j + 1;
      }
    }

    // --- posts --------------------------------------------------------------------------------
    // a post stands `postInset·W` in from its tier's end (the shorter of the two horizontals);
    // with verticalKind 'panel' (FND-25) it is a full-depth inner panel rather than a slim post
    const panelKind = p.verticalKind === 'panel';
    const postD = panelKind ? D : Math.min(p.postDepth, D);
    const inset = fracTicks(W, p.postInset);
    for (let i = 0; i < n; i++) {
      for (const side of ['left', 'right'] as const) {
        const key = side === 'left' ? 'leftPost' : 'rightPost';
        if (!p.tierEnds[i]![key]) continue;
        const x0 = side === 'left' ? Math.max(xs[i]!.x0, xs[i + 1]!.x0) + inset : Math.min(xs[i]!.x1, xs[i + 1]!.x1) - inset - t;
        const id = `post-${side[0]}-${i + 1}`;
        const label = `${side === 'left' ? 'Left' : 'Right'} ${panelKind ? 'inner panel' : 'post'}, tier ${i + 1}`;
        boards.push({ id, role: 'post', label, l: clears[i]!, w: postD, t, grainWorld: 'y', box: { x: x0, y: yTop(i), z: D - postD, sx: t, sy: clears[i]!, sz: postD }, stock: 'carcass', faceSide: 'out' });
        standOn({ id, x0, x1: x0 + t, fix: without(p, i, key) }, i, i + 1, 'dowel-butt', label);
      }
    }

    // --- per-tier dividers (FND-11): full-depth uprights standing in one tier ----------------------
    (p.tierDividers ?? []).forEach((list, i) => {
      if (i >= n) return;
      list.slice().sort((a, b) => a - b).forEach((x, k) => {
        const cx = fracTicks(W, x);
        const x0 = cx - Math.round(t / 2);
        const id = `tdiv-${i + 1}-${k + 1}`;
        const label = `Divider ${k + 1}, tier ${i + 1}`;
        const fix: IssueFix = { label: `Leave out divider ${k + 1} of tier ${i + 1}`, patch: { tierDividers: (p.tierDividers ?? []).map((row, r) => (r === i ? row.filter((v) => v !== x) : row.slice())) } };
        boards.push({ id, role: 'divider', label, l: clears[i]!, w: D, t, grainWorld: 'y', box: { x: x0, y: yTop(i), z: 0, sx: t, sy: clears[i]!, sz: D }, stock: 'carcass', faceSide: 'out' });
        standOn({ id, x0, x1: x0 + t, fix }, i, i + 1, 'dowel-butt', label);
        // another upright in the same place (an end panel, a post or the next divider) is a named error
        for (const q of boards) {
          if (q.id === id || !['end', 'post', 'divider'].includes(q.role) || q.id === 'divider') continue;
          const sameTier = q.box.y < yTop(i) + clears[i]! - 0.5 && q.box.y + q.box.sy > yTop(i) + 0.5;
          if (sameTier && ov(q.box.x, q.box.x + q.box.sx, x0, x0 + t) > -0.5) errors.push({ field: id, message: `${label} stands where ${q.label.toLowerCase()} is.`, fix });
        }
      });
    });

    // an end panel or post standing in the divider's line is a named error (the repair drops it)
    if (divider) {
      for (const q of boards) {
        if ((q.role === 'end' || q.role === 'post' || q.id.startsWith('tdiv-')) && ov(q.box.x, q.box.x + q.box.sx, divider.x0, divider.x1) > 0) {
          const m = /^(end|post)-([lr])-(\d+)$/.exec(q.id);
          const t2 = /^tdiv-(\d+)-/.exec(q.id);
          const fix: IssueFix | undefined = m
            ? without(p, Number(m[3]) - 1, `${m[2] === 'l' ? 'left' : 'right'}${m[1] === 'end' ? 'Panel' : 'Post'}` as keyof Omit<TierEnds, 'backBlock'>)
            : t2 ? { label: `Leave out the dividers of tier ${t2[1]}`, patch: { tierDividers: (p.tierDividers ?? []).map((row, r) => (r === Number(t2[1]) - 1 ? [] : row.slice())) } } : undefined;
          errors.push({ field: q.id, message: `${q.label} runs into the divider.`, ...(fix ? { fix } : {}) });
        }
      }
    }

    // --- back blocks ----------------------------------------------------------------------------
    const bw = fracTicks(W, p.blockWidth);
    for (let i = 0; i < n; i++) {
      const kind = p.tierEnds[i]!.backBlock;
      if (kind === 'none') continue;
      // beside the continuous divider; with none, beside the tier's own divider nearest the middle
      // (the cell's back panel next to its partition); else either side of the centre line
      const own = divider ? null : boards.filter((q) => q.id.startsWith(`tdiv-${i + 1}-`)).sort((a, b) => Math.abs(a.box.x - W / 2) - Math.abs(b.box.x - W / 2))[0] ?? null;
      const cx = divider ?? (own ? { x0: own.box.x, x1: own.box.x + own.box.sx } : { x0: fracTicks(W, 0.5), x1: fracTicks(W, 0.5) });
      const sides = kind === 'full' ? (['l', 'r'] as const) : kind === 'left-of-divider' ? (['l'] as const) : (['r'] as const);
      for (const s of sides) {
        const x0 = s === 'l' ? cx.x0 - bw : cx.x1;
        const id = kind === 'full' ? `block-${i + 1}-${s}` : `block-${i + 1}`;
        const label = `Back block, tier ${i + 1}${kind === 'full' ? (s === 'l' ? ' left' : ' right') : ''}`;
        boards.push({ id, role: 'block', label, l: clears[i]!, w: bw, t, grainWorld: 'y', box: { x: x0, y: yTop(i), z: 0, sx: bw, sy: clears[i]!, sz: t }, stock: 'carcass', faceSide: 'out' });
        standOn({ id, x0, x1: x0 + bw }, i, i + 1, 'screwed-butt', label);
        if (divider) joints.push({ a: id, b: 'divider', kind: 'screwed-butt', face: 'side', datum: yTop(i) });
        else if (own) joints.push({ a: id, b: own.id, kind: 'screwed-butt', face: 'side', datum: yTop(i) });
      }
    }

    // an upright standing where a back block is (a full-depth inner panel or a tier divider reaches
    // the back) is a named error; the fix leaves the upright out
    const blocks = boards.filter((q) => q.role === 'block');
    for (const q of boards) {
      const m = /^(end|post)-([lr])-(\d+)$/.exec(q.id);
      const td = /^tdiv-(\d+)-/.exec(q.id);
      if (!m && !td) continue;
      const hit = blocks.find((k) => ov(q.box.x, q.box.x + q.box.sx, k.box.x, k.box.x + k.box.sx) > 0.5 && ov(q.box.y, q.box.y + q.box.sy, k.box.y, k.box.y + k.box.sy) > 0.5 && ov(q.box.z, q.box.z + q.box.sz, k.box.z, k.box.z + k.box.sz) > 0.5);
      if (!hit) continue;
      const fix: IssueFix = m
        ? without(p, Number(m[3]) - 1, `${m[2] === 'l' ? 'left' : 'right'}${m[1] === 'end' ? 'Panel' : 'Post'}` as keyof Omit<TierEnds, 'backBlock'>)
        : { label: `Leave out the dividers of tier ${td![1]}`, patch: { tierDividers: (p.tierDividers ?? []).map((row, r) => (r === Number(td![1]) - 1 ? [] : row.slice())) } };
      errors.push({ field: q.id, message: `${q.label} runs into ${hit.label.toLowerCase()}.`, fix });
    }

    // --- support, cantilever and span rules -------------------------------------------------------
    // fixes: a post under the free end in the tier below; when that tier already has its post,
    // a shorter overhang; for a long span, a divider under the middle of the gap
    const addPost = (k: number, freeLeft: boolean): IssueFix | null => {
      const tier = k - 1;
      const order: ('leftPost' | 'rightPost')[] = freeLeft ? ['leftPost', 'rightPost'] : ['rightPost', 'leftPost'];
      const key = order.find((o) => !p.tierEnds[tier]![o]);
      if (!key) return null;
      // a full-depth inner panel where a back block stands takes that block's place
      const e = p.tierEnds[tier]!;
      let backBlock = e.backBlock;
      if (panelKind && backBlock !== 'none') {
        const px0 = key === 'leftPost' ? Math.max(xs[tier]!.x0, xs[tier + 1]!.x0) + inset : Math.min(xs[tier]!.x1, xs[tier + 1]!.x1) - inset - t;
        const cx = divider ?? { x0: fracTicks(W, 0.5), x1: fracTicks(W, 0.5) };
        const hitsL = (backBlock === 'full' || backBlock === 'left-of-divider') && ov(px0, px0 + t, cx.x0 - bw, cx.x0) > 0;
        const hitsR = (backBlock === 'full' || backBlock === 'right-of-divider') && ov(px0, px0 + t, cx.x1, cx.x1 + bw) > 0;
        if (hitsL || hitsR) backBlock = backBlock === 'full' ? (hitsL ? 'right-of-divider' : 'left-of-divider') : 'none';
        if (hitsL && hitsR) backBlock = 'none';
      }
      const instead = backBlock !== e.backBlock ? ' in place of the back block' : '';
      return { label: `Add a ${key === 'leftPost' ? 'left' : 'right'} ${p.verticalKind === 'panel' ? 'inner panel' : 'post'} under ${hLabel(k).toLowerCase()}${instead}`, patch: { tierEnds: p.tierEnds.map((x, i) => (i === tier ? { ...x, [key]: true, backBlock } : { ...x })) } };
    };
    for (let k = 1; k <= n; k++) {
      const h = xs[k]!;
      const under = below[k]!;
      const all = [...under, ...above[k]!].sort((a, b) => a.x0 - b.x0);
      const sL = all.length ? Math.min(...all.map((s) => s.x0)) : (h.x0 + h.x1) / 2;
      const sR = all.length ? Math.max(...all.map((s) => s.x1)) : sL;
      const overL = Math.max(0, sL - h.x0), overR = Math.max(0, h.x1 - sR);
      if (under.length < 2) {
        const fix = addPost(k, overL >= overR) ?? addPost(k, overL < overR);
        errors.push({ field: `horizontal-${k}`, message: `${hLabel(k)} rests on ${under.length} support${under.length === 1 ? '' : 's'}; every horizontal needs two.`, ...(fix ? { fix } : {}) });
      }
      if (!all.length) continue;
      const span = sR - sL;
      const over = Math.max(overL, overR);
      if (span > 0 && over / span > maxRatio + 1e-9) {
        const left = overL >= overR;
        // the overhang the limit allows, rounded down to whole millimetres
        const allowed = Math.floor((maxRatio * span) / 10) * 10;
        const hx = left ? { xStart: Math.ceil(((sL - allowed) / W) * 1000) / 1000, xEnd: p.horizontals[k]!.xEnd } : { xStart: p.horizontals[k]!.xStart, xEnd: Math.floor(((sR + allowed) / W) * 1000) / 1000 };
        const shorten: IssueFix = { label: `Shorten ${hLabel(k).toLowerCase()} to a ${fmtMm(allowed)} mm overhang`, patch: { horizontals: p.horizontals.map((x, i) => (i === k ? hx : { ...x })) } };
        const fix = (under.length >= 2 ? null : addPost(k, left)) ?? shorten;
        errors.push({ field: `horizontal-${k}`, message: `${hLabel(k)} overhangs its last support by ${fmtMm(over)} mm, ${Math.round((over / span) * 100)} % of the ${fmtMm(span)} mm it spans; the limit is ${Math.round(maxRatio * 100)} %.`, fix });
      }
      let reach = sL, gap = 0, gapMid = 0;
      for (const s of all) { if (s.x0 - reach > gap) { gap = s.x0 - reach; gapMid = (reach + s.x0) / 2; } reach = Math.max(reach, s.x1); }
      if (gap > maxSpan) {
        const tier = k - 1;
        const x = Math.round((gapMid / W) * 1000) / 1000;
        const tierDividers = Array.from({ length: n }, (_x, i) => (p.tierDividers?.[i] ?? []).slice());
        tierDividers[tier] = [...tierDividers[tier]!, x].sort((a, b) => a - b);
        warnings.push({ field: `horizontal-${k}`, message: `${hLabel(k)} spans ${fmtMm(gap)} mm between supports. In ${fmtMm(t)} mm ${R.kind === 'sheet' ? 'ply' : R.species.toLowerCase()} under a full load of books it may sag; move a post or a back block under it.`, fix: { label: `Add a divider under ${hLabel(k).toLowerCase()}`, patch: { tierDividers } } });
      }
    }

    // --- openings: per tier, either side of the divider (and its own dividers), over the shelf below --
    for (let i = 0; i < n; i++) {
      const floor = xs[i]!;
      const own = boards.filter((q) => q.id.startsWith(`tdiv-${i + 1}-`)).map((q) => ({ x0: q.box.x, x1: q.box.x + q.box.sx }));
      if (!own.length) {
        const cells = divider ? [[0, divider.x0], [divider.x1, W]] : [[0, W]];
        cells.forEach(([a, b], ci) => {
          const x0 = Math.max(a!, floor.x0), x1 = Math.min(b!, floor.x1);
          if (x1 - x0 < 100) return;
          openings.push({ id: `opening-${i + 1}${cells.length > 1 ? (ci === 0 ? '-l' : '-r') : ''}`, label: `Tier ${i + 1}${cells.length > 1 ? (ci === 0 ? ' left' : ' right') : ''}`, x0, x1, y0: yTop(i), y1: levels[i + 1]! });
        });
        continue;
      }
      const cuts = [...own, ...(divider ? [divider] : [])].sort((a, b) => a.x0 - b.x0);
      let at = 0, c = 0;
      for (const cut of [...cuts, { x0: W, x1: W }]) {
        const x0 = Math.max(at, floor.x0), x1 = Math.min(cut.x0, floor.x1);
        at = cut.x1;
        if (x1 - x0 < 100) continue;
        c++;
        openings.push({ id: `opening-${i + 1}-${c}`, label: `Tier ${i + 1}, cell ${c}`, x0, x1, y0: yTop(i), y1: levels[i + 1]! });
      }
    }

    // --- base: plinth rails under the bottom, or levelling feet ----------------------------------------
    const b0 = xs[0]!;
    if (base.kind === 'plinth') {
      const len = b0.x1 - b0.x0;
      for (const [id, label, z] of [['plinth-b', 'Plinth, back', 0], ['plinth-f', 'Plinth, front', D - t - base.setback]] as const) {
        boards.push({ id, role: 'plinth', label, l: len, w: lift, t, grainWorld: 'x', box: { x: b0.x0, y: 0, z, sx: len, sy: lift, sz: t }, stock: 'carcass', faceSide: 'out' });
        joints.push({ a: id, b: 'bottom', kind: 'screwed-butt', face: 'underside', datum: lift });
      }
    } else if (base.kind === 'feet') {
      hardware.push(...feetHardware(lift, [{ id: 'bottom', x0: b0.x0, x1: b0.x1, z0: 0, z1: D }], foot));
    }
  }
  if (H > Math.round(c.warnings.antiTipAboveMm * 10)) warnings.push({ field: 'H', message: `At ${fmtMm(H)} mm tall this must be fixed to the wall (anti-tip).` });

  return assembleBuild({
    familyId: STAGGERED_ID, familyVersion: STAGGERED_VERSION, familyLabel: STAGGERED_LABEL,
    design, stock: S, construction: c, boards, joints, openings, datums, errors, warnings, hardware,
    clearWidth: W - 2 * t, shelves: Math.max(0, n - 1),
  });
}
